import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import { isMobile } from 'react-device-detect';
import { Chart, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { withSnackbarContextActions } from 'Store/SnackbarContext';
import { HeadingOne } from 'Components/Headings';
import ContextSnackbar from '../../../components/Snackbar';
import DashboardFilers from 'Pages/Admin/Dashboard/DashboardFilers';
import { useLazyQuery } from '@apollo/react-hooks';
import { ADMIN_DASHBOARD_DATA } from 'Queries/Admin/adminDashboardData';
import { ReloadOutlined, ExperimentOutlined } from '@ant-design/icons';
import { Button, Card, Alert } from 'antd';
import colors from 'Styles/Colors';
import moment from 'moment';

// Dashboard Components
import TotalRevenueCard from './components/TotalRevenueCard';
import CanceledOrdersCard from './components/CanceledOrdersCard';
import MiscChargesCard from './components/MiscChargesCard';
import AddOnsCard from './components/AddOnsCard';
import InventoryChart from './components/InventoryChart';
import SummaryTabs from './components/SummaryTabs';

// Register Chart.js components
Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const DashboardBase = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({ eventId: '', startDate: '', endDate: '' });
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [errorEvents, setErrorEvents] = useState(false);

  const [getDashboardData, { data, loading, error, refetch }] = useLazyQuery(ADMIN_DASHBOARD_DATA, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (filters.eventId && filters.startDate && filters.endDate) {
      getDashboardData({
        variables: {
          input: {
            eventId: filters.eventId,
            startDate: filters.startDate,
            endDate: filters.endDate
          }
        }
      });
    }
  }, [filters, getDashboardData]);

  useEffect(() => {
    if (data && !loading && !error) {
      console.log('Dashboard Data:', data);
    }

    if (error) {
      console.log('Dashboard Error:', error);
    }
  }, [data, loading, error]);

  const handleReload = () => {
    if (filters.eventId && filters.startDate && filters.endDate && refetch) {
      refetch();
    }
  };

  const hasFiltersApplied = filters.eventId && filters.startDate && filters.endDate;
  const dashboardData = data?.dashboardOrdersStats;

  const renderDashboardContent = () => {
    if (!hasFiltersApplied) {
      return (
        <EmptyState>
          <Card>
            <div className="empty-content">
              <h3>Select Event and Date Range</h3>
              <p>Please select an event and date range above to view dashboard data.</p>
            </div>
          </Card>
        </EmptyState>
      );
    }

    if (loading) {
      return (
        <LoadingState>
          <Card>
            <div className="loading-content">
              <div className="loading-spinner" />
              <h3>Loading Dashboard Data...</h3>
            </div>
          </Card>
        </LoadingState>
      );
    }

    if (error || dashboardData?.error) {
      return (
        <ErrorState>
          <Alert
            message="Error Loading Dashboard"
            description="Something went wrong while loading the dashboard data. Please try again later or contact support if the issue persists."
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={handleReload}>
                Try Again
              </Button>
            }
          />
        </ErrorState>
      );
    }

    if (!dashboardData) {
      return (
        <EmptyState>
          <Card>
            <div className="empty-content">
              <h3>No Data Available</h3>
              <p>No data found for the selected event and date range.</p>
            </div>
          </Card>
        </EmptyState>
      );
    }

    return (
      <DashboardContent>
        {/* Overview Header */}
        <OverviewHeader>
          <div className="overview-title">
            <h1>
              <strong>OVERVIEW</strong> ({dashboardData.event?.name} | {moment(filters.startDate).format('MM/DD/YYYY')} -{' '}
              {moment(filters.endDate).format('MM/DD/YYYY')})
            </h1>
          </div>
        </OverviewHeader>

        {/* First Row - Dashboard Cards */}
        <DashboardRow>
          <TotalRevenueCard data={dashboardData} />
          <CanceledOrdersCard data={dashboardData} />
          <MiscChargesCard data={dashboardData} />
          <AddOnsCard data={dashboardData} />
        </DashboardRow>

        {/* Second Row - Inventory Chart */}
        <DashboardRow>
          <InventoryChart data={dashboardData} />
        </DashboardRow>

        {/* Third Row - Summary Tabs */}
        <DashboardRow>
          <SummaryTabs data={dashboardData} />
        </DashboardRow>
      </DashboardContent>
    );
  };

  if (isMobile) {
    return <Dashboard>Mobile not supported. Please use a desktop browser.</Dashboard>;
  }

  return (
    <Dashboard isMobile={isMobile}>
      <ContextSnackbar />
      <div className="page-header">
        <div className="header-wrapper">
          <HeadingOne label="DASHBOARD" className="heading-one" />
          <div className="beta-notice">
            <ExperimentOutlined /> Beta Version - Contact support if you encounter any issues
          </div>
        </div>
      </div>

      <div className="filters-wrapper">
        <DashboardFilers
          filters={filters}
          setLoadingEvents={setLoadingEvents}
          setErrorEvents={setErrorEvents}
          setFilters={setFilters}
          open={openFilters}
          closeFilters={() => setOpenFilters(false)}
        />
        <div className="reload-button">
          <Button icon={<ReloadOutlined />} onClick={handleReload} disabled={!hasFiltersApplied} title="Reload dashboard data" />
        </div>
      </div>

      <div className="main-wrapper">
        {loadingEvents && <div className="events-loading">Loading events...</div>}
        {errorEvents && <Alert message="Error loading events" description="Error retrieving events, try again later, please." type="error" showIcon />}
        {renderDashboardContent()}
      </div>
    </Dashboard>
  );
};

const Dashboard = styled.div`
  height: 100%;

  .page-header {
    text-align: left;
    border-bottom: 1px solid ${colors.border.primary};
    margin: 0;
  }

  .header-wrapper {
    margin: 85px 22px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .beta-notice {
      color: ${colors.text.secondary};
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .filters-wrapper {
    display: flex;
    justify-content: center;
    padding: 20px;
    width: 100%;
    border-bottom: 1px solid ${colors.border.primary};
    position: relative;

    .reload-button {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .main-wrapper {
    padding: 20px;
    background: ${colors.background.primary};
    min-height: calc(100vh - 200px);
  }

  .events-loading,
  .events-error {
    text-align: center;
    padding: 20px;
    margin-bottom: 20px;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
      margin: 44px 0;
      min-width: 100%;

      .header-wrapper {
        margin: 20px 15px;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }

      .filters-wrapper {
        padding: 15px;

        .reload-button {
          position: static;
          transform: none;
          margin-top: 10px;
        }
      }

      .main-wrapper {
        padding: 15px;
      }
    `}

  ${({ isMobile }) =>
    !isMobile &&
    `
      margin: 85px 0;
      min-width: 100%;
      margin-left: 0;
    `}
`;

const OverviewHeader = styled.div`
  margin-bottom: 24px;

  .overview-title h1 {
    font-size: 24px;
    font-weight: 600;
    color: ${colors.text.primary};
    margin: 0;
    text-transform: uppercase;
  }
`;

const DashboardRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
      flex-direction: column;
      gap: 15px;
    `}
`;

const DashboardContent = styled.div`
  // Dashboard content styles will be handled by individual components
`;

const EmptyState = styled.div`
  background: transparent;
  .ant-card {
    background: transparent;
  }
  .empty-content {
    text-align: center;
    padding: 60px 20px;

    h3 {
      font-size: 18px;
      color: ${colors.text.primary};
      margin-bottom: 8px;
    }

    p {
      color: ${colors.text.secondary};
      font-size: 14px;
    }
  }
`;

const LoadingState = styled.div`
  .loading-content {
    text-align: center;
    padding: 60px 20px;

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid ${colors.border.primary};
      border-top: 3px solid ${colors.primary};
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }

    h3 {
      font-size: 18px;
      color: ${colors.text.primary};
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorState = styled.div`
  // Ant Design Alert component will handle the styling
`;

export default compose(withRouter, withSnackbarContextActions)(memo(DashboardBase));
